import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Store, Loader2 } from "lucide-react";
import { track } from "@/lib/analytics";

const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-[#faf9f5]">
    <div className="hidden lg:flex flex-col justify-between bg-slate-900 p-12 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/40 via-slate-900 to-slate-950" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
          <Store className="w-6 h-6 text-slate-950" />
        </div>
        <span className="font-display font-extrabold text-xl">ConsignOut</span>
      </div>
      <div className="relative">
        <h2 className="font-display text-4xl font-extrabold leading-tight">
          Close your consignment books in minutes, not hours.
        </h2>
        <p className="text-slate-300 mt-4 max-w-md">
          Track consignors, calculate payouts, handle discounts &amp; returns, and generate
          settlement statements — on top of Shopify, WooCommerce, or plain CSV.
        </p>
      </div>
      <div className="relative text-slate-500 text-sm">Consignment settlement, done right.</div>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-sm">
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center">
            <Store className="w-5 h-5 text-slate-950" />
          </div>
          <span className="font-display font-extrabold text-lg">ConsignOut</span>
        </div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900">{title}</h1>
        <p className="text-slate-500 text-sm mt-1 mb-6">{subtitle}</p>
        {children}
      </div>
    </div>
  </div>
);

const Field = (props) => (
  <input {...props}
    className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition" />
);

const Btn = ({ loading, children, ...p }) => (
  <button {...p} disabled={loading || p.disabled}
    className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-2.5 rounded-lg transition disabled:opacity-60">
    {loading && <Loader2 className="w-4 h-4 animate-spin" />} {children}
  </button>
);

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("hello@scrollfinity.com");
  const [password, setPassword] = useState("ConsignClose2026!");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { const u = await login(email, password); navigate(u.onboarded ? "/dashboard" : "/onboarding"); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your ConsignOut account">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {err && <div data-testid="login-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>}
        <div><label className="text-sm font-medium text-slate-700">Email</label>
          <Field data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
        <div><label className="text-sm font-medium text-slate-700">Password</label>
          <Field data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
        <div className="flex justify-end"><Link to="/forgot-password" className="text-sm text-emerald-700 hover:underline" data-testid="forgot-link">Forgot password?</Link></div>
        <Btn data-testid="login-submit" loading={loading}>Sign in</Btn>
        <p className="text-sm text-slate-500 text-center">No account? <Link to="/register" className="text-emerald-700 font-medium hover:underline" data-testid="to-register">Create one</Link></p>
      </form>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { await register(name, email, password); track("signup", "/register"); navigate("/onboarding"); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Create your account" subtitle="Start closing your books in minutes">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {err && <div data-testid="register-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>}
        <div><label className="text-sm font-medium text-slate-700">Full name</label>
          <Field data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required /></div>
        <div><label className="text-sm font-medium text-slate-700">Email</label>
          <Field data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
        <div><label className="text-sm font-medium text-slate-700">Password</label>
          <Field data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} /></div>
        <Btn data-testid="register-submit" loading={loading}>Create account</Btn>
        <p className="text-sm text-slate-500 text-center">Already have an account? <Link to="/login" className="text-emerald-700 font-medium hover:underline" data-testid="to-login">Sign in</Link></p>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    const api = (await import("@/lib/api")).default;
    try { await api.post("/auth/forgot-password", { email }); } catch {}
    setSent(true); setLoading(false);
  };
  return (
    <AuthShell title="Reset your password" subtitle="We'll email you a secure reset link">
      {sent ? (
        <div className="space-y-4" data-testid="forgot-sent">
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-3">
            If that email is registered, a reset link has been sent. It expires in 1 hour.
          </div>
          <Link to="/login" className="text-sm text-emerald-700 hover:underline" data-testid="back-to-login">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div><label className="text-sm font-medium text-slate-700">Email</label>
            <Field data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
          <Btn data-testid="forgot-submit" loading={loading}>Send reset link</Btn>
          <Link to="/login" className="block text-sm text-emerald-700 hover:underline text-center" data-testid="back-to-login">Back to sign in</Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    const api = (await import("@/lib/api")).default;
    try { await api.post("/auth/reset-password", { token, password }); setOk(true); setTimeout(() => navigate("/login"), 1500); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell title="Choose a new password" subtitle="Enter a new password for your account">
      {ok ? (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-3" data-testid="reset-ok">
          Password updated. Redirecting to sign in…
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          {err && <div data-testid="reset-error" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</div>}
          <div><label className="text-sm font-medium text-slate-700">New password</label>
            <Field data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} /></div>
          <Btn data-testid="reset-submit" loading={loading}>Update password</Btn>
          <Link to="/login" className="block text-sm text-emerald-700 hover:underline text-center" data-testid="back-to-login">Back to sign in</Link>
        </form>
      )}
    </AuthShell>
  );
}
